import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { fetchInstruments, Instrument } from '../services/marketDataService';

const InstrumentsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 30px;
  margin: 40px 0;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
    gap: 25px;
    margin: 30px 0;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 20px;
    margin: 25px 0;
  }
`;

const InstrumentCard = styled.div`
  background: rgba(18, 34, 64, 0.6);
  border-radius: 15px;
  padding: 30px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 194, 255, 0.2);
  cursor: pointer;
  
  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 12px 35px rgba(0, 194, 255, 0.4);
    border-color: rgba(0, 194, 255, 0.5);
  }
  
  @media (max-width: 768px) {
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    padding: 20px;
  }
`;

const InstrumentCategory = styled.h3`
  margin: 0 0 20px 0;
  font-size: 1.5rem;
  color: #ffffff;
  display: flex;
  align-items: center;
  padding-bottom: 15px;
  border-bottom: 1px solid rgba(0, 194, 255, 0.3);
  
  &::before {
    content: '';
    display: inline-block;
    width: 12px;
    height: 12px;
    background: linear-gradient(90deg, #00c2ff, #64ffda);
    border-radius: 50%;
    margin-right: 15px;
  }
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
    margin: 0 0 15px 0;
  }
  
  @media (max-width: 480px) {
    font-size: 1.2rem;
  }
`;

const InstrumentList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;
`;

const InstrumentItem = styled.li`
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    color: #64ffda;
  }
  
  @media (max-width: 480px) {
    padding: 10px 0;
  }
`;

const InstrumentName = styled.span`
  font-weight: 500;
  color: #c4cdda;
  
  @media (max-width: 480px) {
    font-size: 0.9rem;
  }
`;

const InstrumentSymbol = styled.span`
  float: right;
  color: #64ffda;
  font-size: 0.9rem;
  background: rgba(0, 194, 255, 0.1);
  padding: 3px 8px;
  border-radius: 10px;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 30px;
  font-size: 1.3rem;
  color: #64ffda;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 20px;
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 30px;
  font-size: 1.3rem;
  color: #ff6b6b;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 12px;
  margin: 30px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
    padding: 25px;
    margin: 25px;
  }
  
  @media (max-width: 480px) {
    font-size: 1.1rem;
    padding: 20px;
    margin: 20px;
  }
`;

const MarketInstruments = () => {
  const [instruments, setInstruments] = useState<Instrument[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupedInstruments, setGroupedInstruments] = useState<{[key: string]: Instrument[]}>({});

  useEffect(() => {
    const loadInstruments = async () => {
      try {
        setLoading(true);
        const data = await fetchInstruments();
        
        // Group instruments by category
        const grouped = data.reduce((acc: {[key: string]: Instrument[]}, instrument) => {
          if (!acc[instrument.category]) {
            acc[instrument.category] = [];
          }
          acc[instrument.category].push(instrument);
          return acc;
        }, {});
        
        setInstruments(data);
        setGroupedInstruments(grouped);
        setLoading(false);
      } catch (err) {
        setError('Failed to load instruments. Please try again later.');
        setLoading(false);
        console.error('Error fetching instruments:', err);
      }
    };

    loadInstruments();
  }, []);

  // Function to format category names for display
  const formatCategoryName = (category: string) => {
    switch (category) {
      case 'indices': return 'Major Indices';
      case 'currency': return 'Currencies';
      case 'commodities': return 'Commodities';
      case 'stocks': return 'Popular Stocks';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  };

  if (loading) {
    return <LoadingMessage>Loading instruments...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>{error}</ErrorMessage>;
  }

  return (
    <InstrumentsGrid>
      {Object.entries(groupedInstruments).map(([category, categoryInstruments]) => (
        <InstrumentCard key={category}>
          <InstrumentCategory>{formatCategoryName(category)}</InstrumentCategory>
          <InstrumentList>
            {categoryInstruments.map((instrument) => (
              <InstrumentItem key={instrument.id}>
                <InstrumentName>{instrument.name}</InstrumentName>
                <InstrumentSymbol>{instrument.symbol}</InstrumentSymbol>
              </InstrumentItem>
            ))}
          </InstrumentList>
        </InstrumentCard>
      ))}
    </InstrumentsGrid>
  );
};

export default MarketInstruments;